import * as React from 'react';

import { useApolloClient } from '@apollo/client';
import type { EnterpriseMessages_Cadence } from '__generated__/graphql-types';

import type { AutoSendCadence } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import ScheduleScopedTriggerDoc from 'bundles/enterprise-admin-messaging-v2/mutations/ScheduleScopedTrigger.graphql';
import UnscheduleScopedTriggerDoc from 'bundles/enterprise-admin-messaging-v2/mutations/UnscheduleScopedTrigger.graphql';
import type {
  ScheduleScopedTriggerMutation,
  ScheduleScopedTriggerMutationVariables,
} from 'bundles/enterprise-admin-messaging-v2/mutations/__generated__/ScheduleScopedTrigger';
import type {
  UnscheduleScopedTriggerMutation,
  UnscheduleScopedTriggerMutationVariables,
} from 'bundles/enterprise-admin-messaging-v2/mutations/__generated__/UnscheduleScopedTrigger';

export type CampaignScheduleUpdate = {
  triggerId: string;
  /** An `AutoSendCadence` starts/reschedules the campaign; `null` pauses it. */
  cadence: AutoSendCadence | null;
};

export type UpdateCampaignScheduleResult = { status: 'success' } | { status: 'error'; error: string };

/**
 * Maps the admin-facing cadence options to the backend's `EnterpriseMessages_Cadence`
 * enum. `15_days` has no exact weekly-aligned equivalent, so it maps to the
 * closest supported member, `BIWEEKLY` (14 days).
 */
export const AUTO_SEND_CADENCE_TO_GQL_CADENCE: Record<AutoSendCadence, EnterpriseMessages_Cadence> = {
  '7_days': 'WEEKLY',
  '15_days': 'BIWEEKLY',
  '30_days': 'MONTHLY',
};

/**
 * Pauses/starts a campaign's trigger schedule via the
 * `EnterpriseMessages_ScheduleScopedTrigger` / `_UnscheduleScopedTrigger`
 * mutations: a real `cadence` schedules (or reschedules) the campaign;
 * `null` unschedules (pauses) it.
 *
 * Call sites use this twice conceptually per resend/send flow: once to pause
 * an old campaign (`{ triggerId, cadence: null }`) and once to start a new one
 * (`{ triggerId, cadence }`), so the payload shape stays `{ triggerId, cadence }`.
 */
export const useUpdateCampaignSchedule = () => {
  const client = useApolloClient();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const updateCampaignSchedule = React.useCallback(
    async ({ triggerId, cadence }: CampaignScheduleUpdate): Promise<UpdateCampaignScheduleResult> => {
      setIsUpdating(true);
      try {
        if (cadence) {
          await client.mutate<ScheduleScopedTriggerMutation, ScheduleScopedTriggerMutationVariables>({
            mutation: ScheduleScopedTriggerDoc,
            variables: { input: { triggerId, cadence: AUTO_SEND_CADENCE_TO_GQL_CADENCE[cadence] } },
            // Routes to the federated gateway (`/graphql-gateway`), where the
            // EnterpriseMessages schema lives — matching this bundle's other mutations.
            context: { clientName: 'gatewayGql' },
            fetchPolicy: 'no-cache',
          });
        } else {
          await client.mutate<UnscheduleScopedTriggerMutation, UnscheduleScopedTriggerMutationVariables>({
            mutation: UnscheduleScopedTriggerDoc,
            variables: { input: { triggerId } },
            context: { clientName: 'gatewayGql' },
            fetchPolicy: 'no-cache',
          });
        }
        return { status: 'success' };
      } catch (e) {
        return { status: 'error', error: e instanceof Error ? e.message : 'Failed to update the campaign schedule.' };
      } finally {
        setIsUpdating(false);
      }
    },
    [client]
  );

  return { updateCampaignSchedule, isUpdating };
};

export default useUpdateCampaignSchedule;
